import type { Stats } from '@/ecs/components/UnitComponents';
import { combatRandom } from '@/network/SeededRNG';

/**
 * Damage calculation result
 */
export interface DamageResult {
  rawDamage: number;
  mitigatedDamage: number;
  finalDamage: number;
  isCritical: boolean;
}

/**
 * Constants for damage calculation
 */
export const DAMAGE_CONSTANTS = {
  BASE_CRIT_CHANCE: 0.1, // 10% base crit chance
  CRIT_MULTIPLIER: 1.5, // 150% damage on crit
  MIN_DAMAGE: 1, // Minimum damage dealt
  DEFENSE_FACTOR: 0.05, // How much each defense point reduces damage
  ABILITY_SCALING: 1.0, // Base ability power scaling
} as const;

/**
 * Calculate physical damage
 */
export function calculatePhysicalDamage(
  attackerStats: Stats,
  defenderStats: Stats,
  bonusMultiplier: number = 1.0
): DamageResult {
  // Base damage from attack stat
  const rawDamage = attackerStats.attack * bonusMultiplier;

  // Defense mitigation (diminishing returns formula)
  const defenseReduction = defenderStats.defense * DAMAGE_CONSTANTS.DEFENSE_FACTOR;
  const mitigationFactor = 1 / (1 + defenseReduction);
  const mitigatedDamage = rawDamage * mitigationFactor;

  // Critical hit check (uses seeded RNG in multiplayer)
  const isCritical = combatRandom() < DAMAGE_CONSTANTS.BASE_CRIT_CHANCE;
  const critMultiplier = isCritical ? DAMAGE_CONSTANTS.CRIT_MULTIPLIER : 1.0;

  // Final damage
  const finalDamage = Math.max(
    DAMAGE_CONSTANTS.MIN_DAMAGE,
    Math.floor(mitigatedDamage * critMultiplier)
  );

  return {
    rawDamage,
    mitigatedDamage,
    finalDamage,
    isCritical,
  };
}

/**
 * Calculate ability damage (magic damage)
 */
export function calculateAbilityDamage(
  attackerStats: Stats,
  baseDamage: number,
  scaling: number = DAMAGE_CONSTANTS.ABILITY_SCALING
): DamageResult {
  // Ability damage scales with ability power
  const rawDamage = baseDamage + attackerStats.abilityPower * scaling;

  // Abilities typically bypass defense (or have reduced mitigation)
  const mitigatedDamage = rawDamage;

  // Critical hit check for abilities (uses seeded RNG in multiplayer)
  const isCritical = combatRandom() < DAMAGE_CONSTANTS.BASE_CRIT_CHANCE;
  const critMultiplier = isCritical ? DAMAGE_CONSTANTS.CRIT_MULTIPLIER : 1.0;

  const finalDamage = Math.max(
    DAMAGE_CONSTANTS.MIN_DAMAGE,
    Math.floor(mitigatedDamage * critMultiplier)
  );

  return {
    rawDamage,
    mitigatedDamage,
    finalDamage,
    isCritical,
  };
}

/**
 * Calculate healing amount
 */
export function calculateHealing(
  healerStats: Stats,
  baseHealing: number,
  scaling: number = 0.5
): number {
  const healing = baseHealing + healerStats.abilityPower * scaling;
  return Math.floor(healing);
}

/**
 * Apply damage to a unit and return if it died
 */
export function applyDamage(targetStats: Stats, damage: number): boolean {
  const newHealth = targetStats.currentHealth - damage;
  // Directly modify the stats object (components are mutable)
  (targetStats as { currentHealth: number }).currentHealth = Math.max(0, newHealth);
  return newHealth <= 0;
}

/**
 * Apply healing to a unit
 */
export function applyHealing(targetStats: Stats, healing: number): void {
  const newHealth = Math.min(
    targetStats.maxHealth,
    targetStats.currentHealth + healing
  );
  (targetStats as { currentHealth: number }).currentHealth = newHealth;
}

/**
 * Check if a unit can attack (based on cooldown and range)
 */
export function canAttack(currentCooldown: number): boolean {
  return currentCooldown <= 0;
}

/**
 * Get attack cooldown duration based on attack speed
 */
export function getAttackCooldown(attackerStats: Stats): number {
  // Attack speed is attacks per second
  // Returns cooldown in seconds
  return 1 / attackerStats.attackSpeed;
}
